import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Aml.css';
import AmlNav from './AmlNav';
import useAmlFramework from './useAmlFramework';
import { canManageAml } from './amlPermissions';
import { listVendorDueDiligence, createVendorDueDiligence, updateVendorDueDiligence, importThirdParties } from './amlApi';

const riskClass = (r) => (r === 'HIGH' ? 'aml-pill-high' : r === 'MEDIUM' ? 'aml-pill-medium' : 'aml-pill-low');

const VENDOR_TYPES = [
  { value: 'CORRESPONDENT_BANK', label: 'Correspondent bank' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'AML_TECH_VENDOR', label: 'AML tech vendor' },
  { value: 'OTHER', label: 'Other' },
];

const emptyForm = { vendorName: '', vendorType: 'CORRESPONDENT_BANK', jurisdiction: '', riskRating: 'MEDIUM', lastReviewDate: '', nextReviewDue: '', notes: '' };

/** FR-17 (remainder): correspondent bank / agent / AML-tech-vendor due-diligence tracking —
 *  a lightweight recordkeeping register, not a connector-backed vendor catalog (see amlApi.js's
 *  comment). Follows the same list+expand-to-edit pattern as AmlCustomerSegments.js. */
export default function AmlVendorDueDiligence() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = canManageAml();
  const { status, frameworks, frameworkId, setFrameworkId } = useAmlFramework();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = () => {
    if (!frameworkId) return;
    setLoading(true);
    listVendorDueDiligence(frameworkId).then((r) => { setRows(r); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [frameworkId]);

  const toggleExpand = (v) => {
    if (expandedId === v.id) { setExpandedId(null); return; }
    setExpandedId(v.id);
    setEditForm({
      vendorName: v.vendorName, vendorType: v.vendorType, jurisdiction: v.jurisdiction || '',
      riskRating: v.riskRating, lastReviewDate: v.lastReviewDate || '', nextReviewDue: v.nextReviewDue || '',
      notes: v.notes || '',
    });
    setEditError('');
  };

  const saveEdit = async (id) => {
    if (!editForm.vendorName.trim()) { setEditError(t('Aml.vendorNameRequired', 'A vendor name is required.')); return; }
    setEditSaving(true);
    setEditError('');
    try {
      await updateVendorDueDiligence(frameworkId, id, editForm);
      load();
    } catch (e) {
      setEditError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the record.'));
    } finally {
      setEditSaving(false);
    }
  };

  const submitAdd = async () => {
    if (!form.vendorName.trim()) { setError(t('Aml.vendorNameRequired', 'A vendor name is required.')); return; }
    setSaving(true);
    setError('');
    try {
      await createVendorDueDiligence(frameworkId, form);
      setShowAdd(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      setError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the record.'));
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const overdueCount = rows.filter((v) => v.nextReviewDue && v.nextReviewDue < today).length;
  const highCount = rows.filter((v) => v.riskRating === 'HIGH').length;

  if (status === 'loading' || loading) return <div className="aml-page"><div className="aml-spinner" /></div>;

  if (status === 'error') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.loadError', 'Could not load the AML Program. Your session may have expired — try reloading.')}
        </div>
      </div>
    );
  }

  if (status === 'not-enrolled') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.notEnrolled', 'Your organization has not enrolled in an AML framework yet. Enroll in SEPS-IGT-2025-0113 or another AML framework from the Frameworks page to activate the AML Program.')}
          <div style={{ marginTop: '0.75rem' }}>
            <button className="aml-btn aml-btn-primary" onClick={() => navigate('/dashboard/compliance/frameworks')}>
              {t('Aml.goToFrameworks', 'Go to Frameworks')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aml-page">
      <div className="aml-header">
        <div>
          <h1 className="aml-title">{t('Aml.vendorDdTitle', 'Vendor & Correspondent Due Diligence')}</h1>
          <p className="aml-subtitle">
            {t('Aml.vendorDdSubtitle', 'Risk-rated register of correspondent banks, agents and AML-tech vendors your program relies on.')}
          </p>
        </div>
      </div>

      <div className="aml-stat-bar">
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statVendorCount', 'TRACKED')}</div><div className="aml-stat-val">{rows.length}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statHighRiskVendors', 'HIGH RISK')}</div><div className="aml-stat-val">{highCount}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statReviewsDue', 'REVIEWS OVERDUE')}</div><div className={`aml-stat-val ${overdueCount > 0 ? 'aml-val-bad' : ''}`}>{overdueCount}</div></div>
      </div>

      <AmlNav frameworks={frameworks} frameworkId={frameworkId} onFrameworkChange={setFrameworkId} />

      <div className="aml-toolbar">
        <div className="aml-toolbar-group" />
        <div className="aml-toolbar-group">
          {canManage && (
            <label className="aml-btn" style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
              {importing ? t('Aml.importing', 'Importing…') : t('Aml.importThirdParties', 'Import third parties (CSV)')}
              <input type="file" accept=".csv" style={{ display: 'none' }} disabled={importing}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  e.target.value = '';
                  if (!file) return;
                  setImporting(true);
                  setImportResult(null);
                  try {
                    setImportResult({ ok: true, ...(await importThirdParties(frameworkId, file)) });
                    load();
                  } catch (err) {
                    setImportResult({ ok: false, message: err?.response?.data?.message || t('Aml.importFailed', 'Could not import the file.') });
                  } finally {
                    setImporting(false);
                  }
                }} />
            </label>
          )}
          {canManage && <button className="aml-btn aml-btn-primary" onClick={() => setShowAdd((s) => !s)}>{t('Aml.addVendor', '+ Add record')}</button>}
        </div>
      </div>

      {importResult && (
        <div className="aml-section" style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem' }}>
          {importResult.ok ? (
            <span>
              {t('Aml.thirdPartyImportSummary', '{{created}} third parties added, {{existing}} already in the register.',
                { created: importResult.created, existing: importResult.existing })}
              {importResult.skipped?.length > 0 && (
                <span className="aml-muted"> · {t('Aml.importSkipped', '{{count}} rows skipped', { count: importResult.skipped.length })}</span>
              )}
            </span>
          ) : (
            <span style={{ color: '#b91c1c' }}>{importResult.message}</span>
          )}
        </div>
      )}

      {showAdd && (
        <div className="aml-section" style={{ padding: '1rem' }}>
          {error && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
            <label>
              <div className="aml-expand-label">{t('Aml.vendorName', 'Name')}</div>
              <input className="aml-input" style={{ width: '100%' }} value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.vendorType', 'Type')}</div>
              <select className="aml-select" style={{ width: '100%' }} value={form.vendorType} onChange={(e) => setForm({ ...form, vendorType: e.target.value })}>
                {VENDOR_TYPES.map((o) => <option key={o.value} value={o.value}>{t(`Aml.vendorType${o.value}`, o.label)}</option>)}
              </select>
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.jurisdiction', 'Jurisdiction')}</div>
              <input className="aml-input" style={{ width: '100%' }} value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.riskRating', 'Risk rating')}</div>
              <select className="aml-select" style={{ width: '100%' }} value={form.riskRating} onChange={(e) => setForm({ ...form, riskRating: e.target.value })}>
                <option value="HIGH">HIGH</option><option value="MEDIUM">MEDIUM</option><option value="LOW">LOW</option>
              </select>
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.lastReviewDate', 'Last review date')}</div>
              <input type="date" className="aml-input" style={{ width: '100%' }} value={form.lastReviewDate} onChange={(e) => setForm({ ...form, lastReviewDate: e.target.value })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.nextReviewDue', 'Next review due')}</div>
              <input type="date" className="aml-input" style={{ width: '100%' }} value={form.nextReviewDue} onChange={(e) => setForm({ ...form, nextReviewDue: e.target.value })} />
              <div className="aml-muted" style={{ fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                {t('Aml.ddScheduleHint', 'Leave empty to schedule by rating: HIGH 12 · MEDIUM 24 · LOW 36 months after the last review.')}
              </div>
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.875rem' }}>
            <button className="aml-btn aml-btn-primary" disabled={saving} onClick={submitAdd}>{t('Aml.save', 'Save')}</button>
            <button className="aml-btn" onClick={() => setShowAdd(false)}>{t('Aml.cancel', 'Cancel')}</button>
          </div>
        </div>
      )}

      <div className="aml-section">
        <div className="aml-table-wrap">
          <table className="aml-table">
            <thead>
              <tr>
                <th>{t('Aml.vendorName', 'NAME')}</th><th>{t('Aml.vendorType', 'TYPE')}</th><th>{t('Aml.jurisdiction', 'JURISDICTION')}</th>
                <th>{t('Aml.riskCol', 'RISK')}</th><th>{t('Aml.lastReviewDate', 'LAST REVIEW')}</th><th>{t('Aml.nextReviewDue', 'NEXT DUE')}</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="aml-empty">{t('Aml.noVendors', 'No correspondent banks, agents or AML-tech vendors tracked yet.')}</td></tr>
              )}
              {rows.map((v) => {
                const overdue = v.nextReviewDue && v.nextReviewDue < today;
                return (
                  <React.Fragment key={v.id}>
                    <tr>
                      <td style={{ fontWeight: 600 }}>
                        {v.vendorName}
                        {v.autoCreated && (
                          <span className="aml-badge aml-pill-muted" style={{ marginLeft: '0.375rem' }}
                            title={t('Aml.ddAutoCreatedNote', 'Created automatically when this vendor was connected')}>
                            {t('Aml.autoGenerated', 'Auto')}
                          </span>
                        )}
                      </td>
                      <td style={{ color: '#475569' }}>{t(`Aml.vendorType${v.vendorType}`, VENDOR_TYPES.find((o) => o.value === v.vendorType)?.label || v.vendorType)}</td>
                      <td>{v.jurisdiction || '—'}</td>
                      <td><span className={`aml-pill ${riskClass(v.riskRating)}`}>{v.riskRating}</span></td>
                      <td>{v.lastReviewDate || '—'}</td>
                      <td style={{ fontWeight: overdue ? 700 : 400, color: overdue ? '#dc2626' : 'inherit' }}>{v.nextReviewDue || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="aml-btn" style={{ height: 30, padding: '0 0.625rem', fontSize: '0.8125rem' }}
                          onClick={() => toggleExpand(v)}>
                          {expandedId === v.id ? t('Aml.hide', 'Hide ▴') : t('Aml.view', 'View ▾')}
                        </button>
                      </td>
                    </tr>
                    {expandedId === v.id && editForm && (
                      <tr className="aml-expand-row">
                        <td colSpan={7}>
                          {canManage ? (
                            <>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                                <label>
                                  <div className="aml-expand-label">{t('Aml.vendorName', 'Name')}</div>
                                  <input className="aml-input" style={{ width: '100%' }} value={editForm.vendorName}
                                    onChange={(e) => setEditForm({ ...editForm, vendorName: e.target.value })} />
                                </label>
                                <label>
                                  <div className="aml-expand-label">{t('Aml.vendorType', 'Type')}</div>
                                  <select className="aml-select" style={{ width: '100%' }} value={editForm.vendorType}
                                    onChange={(e) => setEditForm({ ...editForm, vendorType: e.target.value })}>
                                    {VENDOR_TYPES.map((o) => <option key={o.value} value={o.value}>{t(`Aml.vendorType${o.value}`, o.label)}</option>)}
                                  </select>
                                </label>
                                <label>
                                  <div className="aml-expand-label">{t('Aml.jurisdiction', 'Jurisdiction')}</div>
                                  <input className="aml-input" style={{ width: '100%' }} value={editForm.jurisdiction}
                                    onChange={(e) => setEditForm({ ...editForm, jurisdiction: e.target.value })} />
                                </label>
                                <label>
                                  <div className="aml-expand-label">{t('Aml.riskRating', 'Risk rating')}</div>
                                  <select className="aml-select" style={{ width: '100%' }} value={editForm.riskRating}
                                    onChange={(e) => setEditForm({ ...editForm, riskRating: e.target.value })}>
                                    <option value="HIGH">HIGH</option><option value="MEDIUM">MEDIUM</option><option value="LOW">LOW</option>
                                  </select>
                                </label>
                                <label>
                                  <div className="aml-expand-label">{t('Aml.lastReviewDate', 'Last review date')}</div>
                                  <input type="date" className="aml-input" style={{ width: '100%' }} value={editForm.lastReviewDate}
                                    onChange={(e) => setEditForm({ ...editForm, lastReviewDate: e.target.value })} />
                                </label>
                                <label>
                                  <div className="aml-expand-label">{t('Aml.nextReviewDue', 'Next review due')}</div>
                                  <input type="date" className="aml-input" style={{ width: '100%' }} value={editForm.nextReviewDue}
                                    onChange={(e) => setEditForm({ ...editForm, nextReviewDue: e.target.value })} />
                                </label>
                              </div>
                              <label style={{ display: 'block', marginTop: '0.625rem' }}>
                                <div className="aml-expand-label">{t('Aml.notes', 'Notes')}</div>
                                <textarea className="aml-input" style={{ width: '100%', minHeight: 60 }} value={editForm.notes}
                                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
                              </label>
                              <button className="aml-btn aml-btn-primary" style={{ marginTop: '0.625rem' }} disabled={editSaving}
                                onClick={() => saveEdit(v.id)}>{t('Aml.save', 'Save')}</button>
                              {editError && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginTop: '0.5rem' }}>{editError}</div>}
                            </>
                          ) : (
                            <div style={{ fontSize: '0.8125rem' }}>
                              <div className="aml-expand-label">{t('Aml.notes', 'Notes')}</div>
                              {v.notes || '—'}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="aml-table-foot">
          <span>{t('Aml.showing', '{{count}} of {{total}}', { count: rows.length, total: rows.length })}</span>
        </div>
      </div>
    </div>
  );
}
